import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ThemedPaper } from '@/components/ThemedPaper';
import { DatePicker } from '@/components/DatePicker';
import { PeopleSelector } from '@/components/PeopleSelector';
import { ChildrenSelector } from '@/components/ChildrenSelector';
import { LoadingButton } from '@/components/LoadingButton';
import { FormattedText } from '@/components/FormattedText';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useReservationStore } from '@/store/reservationStore';
import { useTimeslots } from '@/hooks/useTimeslots';
import { getErrorMessage } from '@/utils/error';
import { useStoreInfo } from '@/hooks/useStoreInfo';
import { useDomain } from '@/hooks/useDomain';

/**
 * First step: Select date and number of people
 * User must click Continue to proceed to timeslot selection
 */
export function DatePeopleSelection() {
  const navigate = useNavigate();
  const domain = useDomain();
  const { t } = useTranslation();
  const { date, people, children, setDate, setPeople, setChildren } = useReservationStore();
  const { data: storeInfo } = useStoreInfo(domain);
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    if (!date) {
      setDate(new Date());
    }
  }, [date, setDate]);

  // Reset shouldFetch when date, people, or children change (to allow re-selection)
  useEffect(() => {
    setShouldFetch(false);
  }, [date, people, children]);

  // Only fetch timeslots when user clicks Continue
  const {
    data: timeslots,
    isLoading,
    error,
  } = useTimeslots(domain, shouldFetch ? date : null, people, children);

  const handleContinue = () => {
    if (date && people > 0) {
      // Enable fetching
      setShouldFetch(true);

      // If timeslots are already cached, navigate immediately
      if (timeslots && timeslots.length > 0) {
        const hasAreas = timeslots.some((slot) => slot.area_uuid);
        navigate(hasAreas ? 'seating' : 'timeslots');
      }
    }
  };

  // Navigate when timeslots are loaded (only after user clicks Continue)
  if (shouldFetch && timeslots && timeslots.length > 0) {
    const hasAreas = timeslots.some((slot) => slot.area_uuid);
    navigate(hasAreas ? 'seating' : 'timeslots');
  }

  const canContinue = !!date && people > 0 && !isLoading;

  return (
    <ThemedPaper>
      <Box p={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h5" component="h2">
            {t('datePeopleSelection.title')}
          </Typography>
          <LanguageSelector />
        </Box>

        <Box mt={3} display="flex" flexDirection="column" gap={3}>
          <DatePicker value={date} onChange={(d) => d && setDate(d)} disabled={isLoading} />

          <PeopleSelector value={people} onChange={setPeople} disabled={isLoading} />

          {storeInfo?.allow_children && (
            <ChildrenSelector value={children} onChange={setChildren} disabled={isLoading} />
          )}

          {shouldFetch && error && (
            <Alert severity="error">
              <FormattedText text={getErrorMessage(error, storeInfo?.widget_settings)} />
            </Alert>
          )}

          {shouldFetch && timeslots && timeslots.length === 0 && (
            <Alert severity="info">
              {t('datePeopleSelection.noTimeslots')}
            </Alert>
          )}

          <LoadingButton
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleContinue}
            disabled={!canContinue}
            loading={isLoading}
            endIcon={!isLoading ? <ArrowForward /> : undefined}
          >
            {isLoading ? t('datePeopleSelection.checkingAvailability') : t('common.continue')}
          </LoadingButton>
        </Box>
      </Box>
    </ThemedPaper>
  );
}
